import { useEffect, useState } from "react";
import axios from "axios";
import { Star, BadgeCheck, Quote } from "lucide-react";
import { REVIEWS } from "../data/products";
import Reveal from "../components/Reveal";
import useSiteImages from "../hooks/useSiteImages";
import { toast } from "sonner";
import { API } from "../lib/api";


const Stars = ({ n = 5, size = 14 }) => (
  <div className="flex gap-0.5 text-shay-gold">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={size} fill={i < n ? "currentColor" : "transparent"} strokeWidth={1.2} />
    ))}
  </div>
);

export default function Reviews() {
  const siteImages = useSiteImages();
  const [submittedReviews, setSubmittedReviews] = useState([]);
  const [form, setForm] = useState({ name: "", title: "", body: "", rating: 5 });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews`);
        setSubmittedReviews(data || []);
      } catch (e) {
        // ignore — fallback to seed
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, form);
      setSubmittedReviews((prev) => [data, ...prev]);
      setForm({ name: "", title: "", body: "", rating: 5 });
      toast.success("Review posted", { description: "Thanks for spreading the love." });
    } catch (e) {
      toast.error("Couldn't post review", { description: "Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  const editableSeedReviews = REVIEWS.map((review, index) => ({
    ...review,
    photo: siteImages[`review_${index + 1}`] || review.photo,
  }));
  const allReviews = [...submittedReviews, ...editableSeedReviews];
  const featured = editableSeedReviews[0];
  const rest = allReviews.slice(featured === allReviews[0] ? 1 : 0);

  return (
    <div className="bg-shay-cream min-h-screen pt-32 pb-24" data-testid="reviews-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="text-center mb-16">
          <p className="eyebrow">Customer Reviews</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-shay-navy mt-4 tracking-tight leading-[0.95]">
            Don't take our <span className="italic text-shay-gold">word</span> for it.
          </h1>
          <div className="mt-6 inline-flex items-center gap-3">
            <Stars n={5} size={20} />
            <span className="font-display text-2xl text-shay-navy">4.9 / 5</span>
            <span className="text-sm text-shay-chocolate/60">based on 200+ verified reviews</span>
          </div>
        </div>

        {/* Featured Review */}
        <Reveal>
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-stretch mb-20" data-testid="featured-review">
            <div className="md:col-span-6 zoom-on-hover aspect-[4/3] md:aspect-auto">
              <img src={featured.photo} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
            <div className="md:col-span-6 bg-shay-navy text-shay-cream p-8 md:p-12 flex flex-col justify-center">
              <Quote size={36} className="text-shay-gold mb-5" strokeWidth={1.2} />
              <Stars n={featured.rating} size={18} />
              <h2 className="font-display text-3xl md:text-4xl mt-4 leading-tight">{featured.title}</h2>
              <p className="mt-5 text-shay-cream/80 leading-relaxed">{featured.body}</p>
              <div className="mt-8 flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-shay-gold text-shay-navy grid place-items-center font-display text-xl">
                  {featured.name.charAt(0)}
                </div>
                <div>
                  <p className="font-medium flex items-center gap-1.5">
                    {featured.name} {featured.verified && <BadgeCheck size={14} className="text-shay-gold" />}
                  </p>
                  <p className="text-[11px] uppercase tracking-widest text-shay-cream/50">{featured.date}</p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Review form */}
        <Reveal>
          <form onSubmit={submit} className="bg-white border border-shay-gold/30 p-7 md:p-10 mb-16" data-testid="review-form">
            <h2 className="font-display text-2xl md:text-3xl text-shay-navy">Tasted something good? Tell us.</h2>
            <p className="text-sm text-shay-chocolate/70 mt-1">We read every single review.</p>
            <div className="mt-6 grid md:grid-cols-2 gap-x-6 gap-y-4">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="field-input" data-testid="review-name" />
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-shay-brown">Your rating:</span>
                {[1,2,3,4,5].map((n) => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`review-rating-${n}`}>
                    <Star size={20} className={n <= form.rating ? "text-shay-gold" : "text-shay-navy/20"} fill={n <= form.rating ? "currentColor" : "transparent"} strokeWidth={1.2} />
                  </button>
                ))}
              </div>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Review title" className="field-input md:col-span-2" data-testid="review-title" />
              <textarea required rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Share your experience..." className="field-input md:col-span-2 resize-none" data-testid="review-body" />
            </div>
            <button type="submit" disabled={submitting} className="btn-gold mt-7 disabled:opacity-60" data-testid="review-submit-btn">
              {submitting ? "Posting..." : "Post Review"}
            </button>
          </form>
        </Reveal>

        {/* Review grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="reviews-grid">
          {rest.map((r, i) => (
            <Reveal key={r.id || i} delay={(i % 3) * 0.05}>
              <article className="bg-white border border-shay-gold/20 p-7 h-full flex flex-col" data-testid={`review-card-${r.id || i}`}>
                <Stars n={r.rating} />
                <h3 className="font-display text-xl mt-4 text-shay-navy leading-snug">{r.title}</h3>
                <p className="mt-3 text-sm text-shay-chocolate/75 leading-relaxed flex-1">{r.body}</p>
                {r.photo && (
                  <div className="mt-5 aspect-[5/3] overflow-hidden">
                    <img src={r.photo} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </div>
                )}
                <div className="mt-5 pt-4 border-t border-shay-gold/15 flex items-center justify-between">
                  <p className="font-medium text-sm flex items-center gap-1.5">
                    {r.name} {r.verified && <BadgeCheck size={13} className="text-shay-gold" />}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-shay-chocolate/50">{r.date}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
